/**
 * @fileoverview Actions Searcher Module.
 * This module sets up and initializes the Algolia InstantSearch for the actions section.
 * It includes configurations for search parameters, custom rendering of search hits, and pagination.
 */

import { elementExists, getFullIndexName } from '../Utils';
import instantsearch from 'instantsearch.js';
import { configure, refinementList, searchBox } from 'instantsearch.js/es/widgets';
import { currentRefinementsComponent } from "./widgets/ListClearRefinements";
import algoliasearch from 'algoliasearch';
import { connectHits } from "instantsearch.js/es/connectors";
import customPagination from './widgets/CustomPagination';
import ActionHits from "./widgets/ActionHits";
import { gtmSendSearchData, setInitialFiltersCounter, updateResultCounter, urlUpdateHandler } from "./Utils";
import { debounceEvent } from "../Utils/debounceEvent";
import SelectedFilters from "./widgets/SelectedFilters";

/**
 * @constant
 * @type {object}
 * @description Algolia search client instance, configured with application ID and search-only API key.
 */
const searchClient = algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, import.meta.env.VITE_ALGOLIA_READ_KEY);

const actionsContainerSelector = '#actions-container';
const valueChainRefinementSelector = '#value-chain-refinement-container';
const howToRefinementSelector = '#how-to-refinement-container';
const searchboxSelector = '#searchbox';
const clearFiltersLabel = 'Filter By';
const hitsSelector = '#actions_hits';
const indexName = 'actions';
const paginationSelector = '#pagination';
let dataLayerTimeout = null;
const selectedFiltersSelector = '#selected-filters-container';

/**
 * Initializes the actions searcher module.
 * Checks if the actions container exists and initializes the searcher if it does.
 */
const init = () => {
    if (!elementExists(actionsContainerSelector)) return;
    setListeners();
    let viewportHitsPerPage = window.visualViewport.width > 768 ? 24 : 12;
    initSearcher(viewportHitsPerPage);
};

const setListeners = () => {
    document.addEventListener('urlUpdateEvent', urlUpdateHandler);
}


/**
 * Initializes and configures the Algolia InstantSearch for the actions section.
 * Sets up the search parameters, custom widgets, and starts the search process.
 */
const initSearcher = (hitsPerPage) => {
    const search = instantsearch({
        indexName: getFullIndexName(indexName),
        searchClient,
        routing: {
            stateMapping: {
                stateToRoute(uiState) {
                    const indexUiState = uiState[getFullIndexName(indexName)] || {};

                    let routeState = {};
                    if (indexUiState.query) {
                        routeState.q = indexUiState.query;
                    }
                    if (indexUiState.refinementList?.value_chain) {
                        routeState.vc = indexUiState.refinementList.value_chain.join(',');
                    }
                    if (indexUiState.refinementList?.thematic) {
                        routeState.th = indexUiState.refinementList.thematic.join(',');
                    }
                    routeState.pg = indexUiState.page;
                    debounceEvent('urlUpdateEvent', routeState);
                    return routeState;
                },
                routeToState(routeState) {
                    let state = {
                        [getFullIndexName(indexName)]: {
                            query: routeState.q || '',
                            refinementList: {},
                            page: routeState.pg || 1
                        }
                    };

                    if (routeState.vc) {
                        state[getFullIndexName(indexName)].refinementList.value_chain = routeState.vc.split(',').map(decodeURIComponent);
                    }

                    if (routeState.th) {
                        state[getFullIndexName(indexName)].refinementList.thematic = routeState.th.split(',').map(decodeURIComponent);
                    }

                    state[getFullIndexName(indexName)].page = routeState.pg || 1;

                    return state;
                }
            }
        },
    });

    /**
     * Connects hits from Algolia search to a custom rendering function.
     * Customizes the display of hits using `ActionHits` and sets initial filters for value chain and thematic.
     * Also sends search data to Google Tag Manager for tracking.
     * @type {function}
     */
    const customHits = connectHits((hits) => {
        ActionHits(hits);
        setInitialFiltersCounter(hits.instantSearchInstance.helper?.state.disjunctiveFacetsRefinements, 'value_chain');
        setInitialFiltersCounter(hits.instantSearchInstance.helper?.state.disjunctiveFacetsRefinements, 'thematic');
        updateResultCounter(hits);
        dataLayerTimeout = gtmSendSearchData(dataLayerTimeout, hits, 'actions-search-ev');
    });

    search.addWidgets([
        configure({
            hitsPerPage: hitsPerPage,
        }),
        searchBox({
            container: searchboxSelector,
            placeholder: 'Search an action',
            showSubmit: true,
            templates: {
                submit: '<div class="absolute left-3 top-6"><svg class="h-[18.28px]" width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">\n' +
                    '<path d="M12.877 13.7927L17.612 18.5798" stroke="black"/>\n' +
                    '<path d="M13.73 7.13414C13.73 10.6401 10.9202 13.4729 7.46557 13.4729C4.01097 13.4729 1.20117 10.6401 1.20117 7.13414C1.20117 3.62819 4.01097 0.79541 7.46557 0.79541C10.9202 0.79541 13.73 3.62819 13.73 7.13414Z" stroke="#2A2824"/>\n' +
                    '</svg></div>'
            },
        }),
        refinementList({
            container: valueChainRefinementSelector,
            sortBy: ['name:asc'],
            attribute: 'value_chain',
            limit: 100
        }),
        refinementList({
            container: howToRefinementSelector,
            sortBy: ['name:asc'],
            attribute: 'thematic',
            limit: 100
        }),
        customHits({
            container: document.querySelector(hitsSelector),
        }),
        customPagination({
            container: document.querySelector(paginationSelector),
            topContainer: 'actions-search-container',
        }),
        SelectedFilters({
            container: document.querySelector(selectedFiltersSelector),
        })
    ]);

    search.start();
};

init();